import {
  useSnackbarController,
  type SelectionController,
  type Entity,
} from "@firecms/core";
import { Button } from "@firecms/ui";
import { useCallback, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import {
  getModeCollectionIds,
  type ModeCollectionIds,
} from "../workspace/adminMode";
import { useAdminWorkspace } from "../workspace/useAdminWorkspace";
import { toCallableWorkspaceParams } from "../workspace/callableParams";

type FunctionButtonProps = {
  functionName: string;
  selectionController: SelectionController;
  label: string;
  loadingLabel: string;
  multiple?: boolean;
  withSelection?: boolean;
  params?: (
    ids: ModeCollectionIds,
    selectedEntities: Entity<any>[]
  ) => Record<string, unknown>;
};

const defaultParams = (
  ids: ModeCollectionIds,
  selectedEntities: Entity<any>[]
): Record<string, unknown> => {
  const base = toCallableWorkspaceParams(ids);

  if (!selectedEntities.length) {
    return { ...base, documentIds: [] };
  }

  if (selectedEntities.length > 1) {
    return {
      ...base,
      documentIds: selectedEntities.map((entity) => entity.id),
    };
  }

  return { ...base, documentIds: [selectedEntities[0]!.id] };
};

export function FunctionButton({
  functionName,
  selectionController,
  withSelection = true,
  label,
  loadingLabel,
  multiple,
  params = defaultParams,
}: FunctionButtonProps) {
  const [loading, setLoading] = useState(false);
  const { mode } = useAdminWorkspace();
  const workspaceIds = useMemo(() => getModeCollectionIds(mode), [mode]);
  const functions = useFirebaseFunctions();
  const snackbarController = useSnackbarController();

  const handleClick = useCallback(async () => {
    if (!functions) {
      snackbarController.open({
        message: "Internal error",
        type: "warning",
      });
      return;
    }

    if (withSelection) {
      if (selectionController.selectedEntities.length === 0) {
        snackbarController.open({
          message: "You need to select at least one entity",
          type: "warning",
        });
        return;
      }

      if (!multiple && selectionController.selectedEntities.length > 1) {
        snackbarController.open({
          message: "You can only do one entity at a time",
          type: "warning",
        });
        return;
      }
    }

    const callable = httpsCallable(functions, functionName);

    try {
      setLoading(true);
      await callable(
        params(workspaceIds, selectionController.selectedEntities)
      );
      snackbarController.open({
        message: "Operation successful",
        type: "success",
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Unknown error";
      snackbarController.open({
        message: `Operation failed: ${message}`,
        type: "error",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    functions,
    functionName,
    multiple,
    params,
    selectionController.selectedEntities,
    snackbarController,
    withSelection,
    workspaceIds,
  ]);

  const countText = withSelection
    ? ` (${selectionController.selectedEntities.length})`
    : "";
  const permitted =
    (multiple && selectionController.selectedEntities.length > 0) ||
    (!multiple && selectionController.selectedEntities.length === 1) ||
    !withSelection;

  return (
    <Button
      onClick={handleClick}
      variant="outlined"
      disabled={loading || !permitted}
    >
      {loading ? `${loadingLabel}...` : label + countText}
    </Button>
  );
}
